import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBolt,
  faComments,
  faGlobe,
  faMessage,
  faPlus,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import api, { getData } from "../services/api";
import { compactNumber, formatDate } from "../utils/format";
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from "../components/UI";

const kpis = [
  { key: "totalWebsites", label: "Total websites", icon: faGlobe },
  { key: "activeWebsites", label: "Active websites", icon: faBolt },
  { key: "totalSessions", label: "Chat sessions", icon: faComments },
  { key: "totalMessages", label: "Total messages", icon: faMessage },
];
export default function DashboardPage() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    api
      .get("/analytics/dashboard")
      .then((r) => setState({ loading: false, data: getData(r), error: "" }))
      .catch((e) => setState({ loading: false, data: null, error: e.message }));
  }, []);
  useEffect(load, [load]);
  const data = state.data;
  return (
    <>
      <PageHeader
        eyebrow="Operations overview"
        title="Good to see you."
        description="A clear view of your website assistants and the questions they handle."
        actions={
          <Button
            icon={faPlus}
            onClick={() => navigate("/websites", { state: { add: true } })}
          >
            Add website
          </Button>
        }
      />
      {state.loading ? (
        <Skeleton rows={6} />
      ) : state.error ? (
        <ErrorState message={state.error} onRetry={load} />
      ) : (
        <>
          <section className="kpi-grid">
            {kpis.map((item, index) => (
              <article className="kpi-card" key={item.key}>
                <span className={`signal-rail rail-${index}`} />
                <div className="kpi-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <span>{item.label}</span>
                <strong>{compactNumber(data[item.key])}</strong>
              </article>
            ))}
          </section>
          <section className="dashboard-grid">
            <article className="panel chart-panel">
              <header>
                <div>
                  <span className="eyebrow">Last 30 days</span>
                  <h2>Conversation activity</h2>
                </div>
                <div className="chart-legend">
                  <span>
                    <i className="indigo" />
                    Messages
                  </span>
                  <span>
                    <i className="mint" />
                    Sessions
                  </span>
                </div>
              </header>
              {data.trend?.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data.trend}>
                    <defs>
                      <linearGradient id="messages" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0"
                          stopColor="#556ee6"
                          stopOpacity={0.22}
                        />
                        <stop offset="1" stopColor="#556ee6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e7eaf0"
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => formatDate(v, true)}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip labelFormatter={(v) => formatDate(v)} />
                    <Area
                      type="monotone"
                      dataKey="messages"
                      stroke="#556ee6"
                      strokeWidth={2.5}
                      fill="url(#messages)"
                    />
                    <Area
                      type="monotone"
                      dataKey="sessions"
                      stroke="#39a981"
                      strokeWidth={2}
                      fillOpacity={0}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  icon={faComments}
                  title="No conversations yet"
                  message="Activity will appear after visitors begin using an installed chatbot."
                />
              )}
            </article>
            <aside className="panel pulse-panel">
              <span className="eyebrow">Question pulse</span>
              <h2>At a glance</h2>
              {[
                ["Today", data.questionsToday],
                ["This week", data.questionsWeek],
                ["This month", data.questionsMonth],
              ].map(([label, value]) => (
                <div className="pulse-row" key={label}>
                  <span>{label}</span>
                  <strong>{compactNumber(value)}</strong>
                </div>
              ))}
              <div className="quick-actions">
                <h3>Quick actions</h3>
                <button onClick={() => navigate("/websites")}>
                  <FontAwesomeIcon icon={faGlobe} />
                  <span>
                    <strong>Manage websites</strong>
                    <small>Tenants, domains, status</small>
                  </span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
                <button onClick={() => navigate("/knowledge")}>
                  <FontAwesomeIcon icon={faUpload} />
                  <span>
                    <strong>Import knowledge</strong>
                    <small>Upload versioned JSON</small>
                  </span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </aside>
          </section>
        </>
      )}
    </>
  );
}
